import { useState } from "react";
import {
  User,
  Calendar,
  MapPin,
  Edit3,
  Loader2,
  Briefcase,
  Users,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { useCurrentUser } from "@/pages/auth/hooks/useAuth";
import { useCandidateProfileById } from "@/pages/candidate-profile/hooks/useCandidateProfile";
import ProfileEditModal from "@/pages/profile/employee/ProfileEditModal";

export default function PersonalTab({
  profileId,
  onEditClick,
  source = "profile",
}: {
  profileId?: number | string;
  onEditClick?: () => void;
  source?: "profile" | "other";
} = {}) {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const { data: user, isLoading: isUserLoading } = useCurrentUser();
  const targetId = profileId || user?.id;

  const { data: candidateProfileRes, isLoading: isCandLoading } =
    useCandidateProfileById(targetId, Boolean(targetId));

  const activeUser = candidateProfileRes?.data || user;
  const isLoading = (isCandLoading || isUserLoading) && !activeUser;

  const fullName =
    (activeUser?.first_name
      ? `${activeUser.first_name} ${activeUser.last_name ?? ""}`.trim()
      : "") ||
    activeUser?.username ||
    "N/A";

  const handleEditClick = () => {
    if (onEditClick) {
      onEditClick();
    } else {
      setIsModalOpen(true);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
          <User className="w-5 h-5 text-purple-600 dark:text-purple-400" /> Personal Information
        </h2>
        <Button
          onClick={handleEditClick}
          className="bg-purple-600 hover:bg-purple-700 text-white"
        >
          <Edit3 className="w-4 h-4 mr-2" /> Edit Personal Info
        </Button>
      </div>

      {isLoading ? (
        <div className="flex justify-center items-center p-12">
          <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Basic Identification */}
          <div className="p-6 rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-sm space-y-4">
            <h3 className="text-sm font-bold text-purple-900 dark:text-purple-300 uppercase tracking-wider border-b border-slate-100 dark:border-slate-800 pb-3 flex items-center gap-2">
              <User className="w-4 h-4 text-purple-500" /> Basic Details
            </h3>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between">
                <span className="text-slate-500 dark:text-slate-400 font-medium">Full Name</span>
                <span className="font-semibold text-slate-900 dark:text-white">{fullName}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500 dark:text-slate-400 font-medium">Job Role</span>
                <span className="font-semibold text-slate-900 dark:text-white">
                  {activeUser?.job_role || "N/A"}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500 dark:text-slate-400 font-medium">
                  Total Experience
                </span>
                <span className="font-semibold text-slate-900 dark:text-white">
                  {activeUser?.experience_years ?? 0} yrs{" "}
                  {activeUser?.experience_months ?? 0} mos
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500 dark:text-slate-400 font-medium">
                  Date of Birth
                </span>
                <span className="font-semibold text-slate-900 dark:text-white flex items-center gap-1">
                  <Calendar className="w-3.5 h-3.5 text-slate-400" />
                  {activeUser?.birth_date || "N/A"}
                </span>
              </div>
            </div>
          </div>

          {/* Department & Management Info */}
          <div className="p-6 rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-sm space-y-4">
            <h3 className="text-sm font-bold text-purple-900 dark:text-purple-300 uppercase tracking-wider border-b border-slate-100 dark:border-slate-800 pb-3 flex items-center gap-2">
              <Briefcase className="w-4 h-4 text-purple-500" /> Organization
              Details
            </h3>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between">
                <span className="text-slate-500 dark:text-slate-400 font-medium">Department</span>
                <span className="font-semibold text-slate-900 dark:text-white">
                  {activeUser?.department_detail?.department_name || "N/A"}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500 dark:text-slate-400 font-medium">Team</span>
                <span className="font-semibold text-slate-900 dark:text-white">
                  {activeUser?.team_detail?.team_name || "N/A"}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500 dark:text-slate-400 font-medium flex items-center gap-1">
                  <Users className="w-3.5 h-3.5 text-slate-400" /> Reporting
                  Manager
                </span>
                <span className="font-semibold text-slate-900 dark:text-white">
                  {activeUser?.reporting_manager_detail?.name || "N/A"}
                </span>
              </div>
            </div>
          </div>

          {/* Location & Address */}
          <div className="p-6 rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-sm space-y-4">
            <h3 className="text-sm font-bold text-purple-900 dark:text-purple-300 uppercase tracking-wider border-b border-slate-100 dark:border-slate-800 pb-3 flex items-center gap-2">
              <MapPin className="w-4 h-4 text-purple-500" /> Address Details
            </h3>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between">
                <span className="text-slate-500 dark:text-slate-400 font-medium">
                  Street Address
                </span>
                <span className="font-semibold text-slate-900 dark:text-white">
                  {activeUser?.address || "N/A"}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500 dark:text-slate-400 font-medium">City / State</span>
                <span className="font-semibold text-slate-900 dark:text-white">
                  {activeUser?.city || activeUser?.state
                    ? `${activeUser?.city ?? ""}${activeUser?.city && activeUser?.state ? ", " : ""}${activeUser?.state ?? ""}`
                    : "N/A"}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500 dark:text-slate-400 font-medium">Country</span>
                <span className="font-semibold text-slate-900 dark:text-white">
                  {activeUser?.country || "N/A"}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500 dark:text-slate-400 font-medium">Postal Code</span>
                <span className="font-semibold text-slate-900 dark:text-white">
                  {activeUser?.postal_code || "N/A"}
                </span>
              </div>
            </div>
          </div>

          {/* Summary / Bio */}
          {activeUser?.summary && (
            <div className="md:col-span-2 p-6 rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-sm space-y-3">
              <h3 className="text-sm font-bold text-purple-900 dark:text-purple-300 uppercase tracking-wider border-b border-slate-100 dark:border-slate-800 pb-3">
                Professional Summary
              </h3>
              <p className="text-sm text-slate-700 dark:text-slate-300 leading-relaxed whitespace-pre-line">
                {activeUser.summary}
              </p>
            </div>
          )}
        </div>
      )}

      <ProfileEditModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        profileData={activeUser as any}
        source={source}
      />
    </div>
  );
}
